/**
 * API клієнт для doc.retail_sales (Порція Роздріб.3/5a).
 * Backend: /api/v1/retail/retail-sales.
 *
 * Permissions:
 *  - retail.sale.create — POST/PATCH/DELETE/GET
 *  - retail.sale.post — /post, /unpost
 *  - retail.sale.fiscalize — /fiscalize
 *  - retail.sale.void — /void
 *
 * Поведінка auto_post (рішення власника β, §4.5):
 *  - is_backdated=false → auto-POST у тій самій транзакції
 *  - is_backdated=true → чернетка (бух-UI flow), окремий /post
 */
import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

export type FiscalStatus = 'not_fiscalized' | 'pending' | 'fiscalized' | 'failed' | 'demo';
export type RetailSaleStatus = 'draft' | 'posted' | 'voided';
export type RetailPaymentMethod = 'cash' | 'card' | 'mixed' | 'transfer';
export type ItemKind = 'goods' | 'service';

export interface RetailSaleItemIn {
  line_number: number;
  nomenclature_id: string;
  item_kind: ItemKind;
  quantity: string;
  unit_id?: string | null;
  price_per_unit?: string | null;
  price_group_id?: string | null;
  discount_percent?: string;
  discount_amount?: string;
  vat_rate?: string;
}

export interface RetailSaleItem {
  id: string;
  line_number: number;
  nomenclature_id: string;
  item_kind: ItemKind;
  quantity: string;
  unit_id: string | null;
  price_per_unit: string;
  price_group_id: string | null;
  discount_percent: string | null;
  discount_amount: string | null;
  subtotal: string | null;
  total: string | null;
  vat_rate: string | null;
  vat_amount: string | null;
  cost_per_unit: string | null;
  cost_total: string | null;
}

export interface RetailSaleListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  pos_workstation_id: string;
  cashier_user_id: string;
  customer_card_id: string | null;
  subtotal: string;
  discount_amount: string;
  total_amount: string;
  payment_method: RetailPaymentMethod;
  fiscal_status: FiscalStatus;
  fiscal_number: string | null;
  posted: boolean;
  status: RetailSaleStatus;
  is_backdated: boolean;
  deletion_mark: boolean;
  // Block 5.3c: CancellableMixin state (coexist з retail status).
  state?: CancellableState;
}

export interface RetailSale extends RetailSaleListItem {
  sales_channel_id: string | null;
  bonus_writeoff_amount: string;
  vat_amount: string;
  cash_amount: string;
  card_amount: string;
  change_amount: string;
  bonus_accrual_amount: string;
  fiscal_url: string | null;
  fiscal_pos_provider_id: string | null;
  fiscalized_at: string | null;
  fiscalization_error: string | null;
  posted_at: string | null;
  backdate_reason: string | null;
  created_at: string;
  created_by: string | null;
  comment: string | null;
  items: RetailSaleItem[];
  // Block 5.3c:
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface RetailSaleCreate {
  organization_id: string;
  document_number?: string | null;
  document_date?: string | null;
  pos_workstation_id: string;
  sales_channel_id?: string | null;
  customer_card_id?: string | null;
  payment_method?: RetailPaymentMethod;
  cash_amount?: string;
  card_amount?: string;
  bonus_writeoff_amount?: string;
  change_amount?: string;
  discount_amount?: string;
  is_backdated?: boolean;
  backdate_reason?: string | null;
  items: RetailSaleItemIn[];
  comment?: string | null;
}

export type RetailSaleUpdate = Partial<Omit<RetailSaleCreate, 'organization_id' | 'pos_workstation_id'>>;

export interface RetailSaleFiscalizeOut {
  document: RetailSale;
  fiscal_status: FiscalStatus;
  fiscal_number: string | null;
  fiscal_url: string | null;
  fiscalized_at: string | null;
  error_message?: string | null;
}

export interface RetailSaleFiscalStatusOut {
  document_id: string;
  fiscal_status: FiscalStatus;
  fiscal_number: string | null;
  receipt_uid: string | null;
  is_in_dps: boolean;
  dps_status: string | null;
  last_checked_at: string;
}

export const retailSales = {
  list: (params?: {
    organization_id?: string;
    pos_workstation_id?: string;
    customer_card_id?: string;
    fiscal_status?: FiscalStatus;
    posted?: boolean;
    limit?: number;
    offset?: number;
  }) =>
    api.get<Page<RetailSaleListItem>>('/retail/retail-sales', { params }).then((r) => r.data),
  get: (id: string) => api.get<RetailSale>(`/retail/retail-sales/${id}`).then((r) => r.data),
  create: (payload: RetailSaleCreate) =>
    api.post<RetailSale>('/retail/retail-sales', payload).then((r) => r.data),
  update: (id: string, payload: RetailSaleUpdate) =>
    api.patch<RetailSale>(`/retail/retail-sales/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`/retail/retail-sales/${id}`).then(() => undefined),
  post: (id: string) => api.post<RetailSale>(`/retail/retail-sales/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<RetailSale>(`/retail/retail-sales/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/retail/retail-sales/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
  fiscalize: (id: string) =>
    api.post<RetailSaleFiscalizeOut>(`/retail/retail-sales/${id}/fiscalize`).then((r) => r.data),
  void: (id: string) =>
    api.post<RetailSale>(`/retail/retail-sales/${id}/void`).then((r) => r.data),
  checkFiscalStatus: (id: string) =>
    api.get<RetailSaleFiscalStatusOut>(`/retail/retail-sales/${id}/check-fiscal-status`).then((r) => r.data),
};
